import React from 'react';
import { Alert, Box, Button, Card, CardContent, Paper, Stack, Typography } from '@mui/material';
import type { WorkflowBlocker } from '../../utils/workflowLanes';

type LaneBlockersPanelProps = {
  title?: string;
  subtitle?: string;
  blockers: WorkflowBlocker[];
  warningText?: string | null;
  emptyText?: string;
  onNavigate?: (path: string) => void;
};

export function LaneBlockersPanel({
  title = 'Current blockers',
  subtitle = 'Resolve the active blockers before continuing.',
  blockers,
  warningText,
  emptyText = 'No current blockers are preventing progress.',
  onNavigate,
}: LaneBlockersPanelProps) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          {warningText ? <Alert severity="warning">{warningText}</Alert> : null}

          {blockers.length > 0 ? (
            <Stack spacing={1}>
              {blockers.map((blocker) => (
                <Paper key={blocker.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" gap={2} alignItems="center" flexWrap="wrap">
                    <Box sx={{ flex: '1 1 280px' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                        {blocker.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {blocker.detail}
                      </Typography>
                    </Box>
                    {blocker.actionPath && blocker.actionLabel && onNavigate ? (
                      <Button size="small" variant="outlined" onClick={() => onNavigate(blocker.actionPath!)}>
                        {blocker.actionLabel}
                      </Button>
                    ) : null}
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Alert severity="success">{emptyText}</Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
